/**
 * The function registry: the authoritative definition of the Core_Formula_Set
 * (Requirement 4.1).
 *
 * One `Map<string, FnSpec>` holds all 75 functions. `FnSpec` declares the
 * contract the evaluator enforces before a body ever runs — argument count,
 * whether the function inspects error arguments itself, whether its result is an
 * array that spills, and whether it is volatile — so arity and error handling are
 * uniform instead of re-implemented 75 times.
 *
 * Lookup is case-insensitive because the parser upper-cases call names, and an
 * unknown name yields `#NAME?` in the evaluator rather than throwing
 * (Requirement 4.9). `functionNames(prefix)` backs the formula-editing
 * autocomplete of Requirement 2.10.
 *
 * There is no dynamic dispatch through generated code anywhere in this path: the
 * registry maps a name to an ordinary function value, which is what lets the
 * shipped bundle stay free of `eval` and `new Function`.
 */

import { ARRAY_FUNCTIONS } from './functions/array.js';
import { DATETIME_FUNCTIONS } from './functions/datetime.js';
import { INFO_FUNCTIONS } from './functions/info.js';
import { LOGICAL_FUNCTIONS } from './functions/logical.js';
import { LOOKUP_FUNCTIONS } from './functions/lookup.js';
import { MATH_FUNCTIONS } from './functions/math.js';
import { TEXT_FUNCTIONS } from './functions/text.js';
import { VARIADIC, type FnSpec } from './values.js';

export type { FnSpec } from './values.js';
export { VARIADIC } from './values.js';

/** Every group, in the order the design document lists them. */
const GROUPS: readonly (readonly FnSpec[])[] = [
  MATH_FUNCTIONS,
  LOGICAL_FUNCTIONS,
  LOOKUP_FUNCTIONS,
  TEXT_FUNCTIONS,
  DATETIME_FUNCTIONS,
  INFO_FUNCTIONS,
  ARRAY_FUNCTIONS,
];

function buildRegistry(): Map<string, FnSpec> {
  const map = new Map<string, FnSpec>();
  for (const group of GROUPS) {
    for (const spec of group) {
      const key = spec.name.toUpperCase();
      if (map.has(key)) throw new Error(`duplicate function in registry: ${key}`);
      if (spec.min < 0 || spec.max < spec.min || spec.max > VARIADIC) {
        throw new Error(`invalid arity for ${key}: ${spec.min}..${spec.max}`);
      }
      map.set(key, spec);
    }
  }
  return map;
}

/** The Core_Formula_Set, keyed by upper-case name. */
export const REGISTRY: ReadonlyMap<string, FnSpec> = buildRegistry();

/** Resolves a function name, case-insensitively. `undefined` means `#NAME?`. */
export function lookupFunction(name: string): FnSpec | undefined {
  return REGISTRY.get(name.toUpperCase());
}

export function hasFunction(name: string): boolean {
  return REGISTRY.has(name.toUpperCase());
}

/** Every registered name, sorted. */
export function allFunctionNames(): string[] {
  return [...REGISTRY.keys()].sort();
}

/**
 * Names matching a typed prefix, sorted, for the editing-time autocomplete of
 * Requirement 2.10. An empty prefix lists everything.
 */
export function functionNames(prefix: string): string[] {
  const folded = prefix.toUpperCase();
  return allFunctionNames().filter((name) => name.startsWith(folded));
}

/** Declared argument range of a function, for tests and for the UI hint text. */
export function arityOf(name: string): { min: number; max: number } | null {
  const spec = lookupFunction(name);
  return spec === undefined ? null : { min: spec.min, max: spec.max };
}

/** Functions that must be recomputed every Recalculation_Cycle (`TODAY`, `NOW`). */
export function volatileFunctionNames(): string[] {
  return allFunctionNames().filter((name) => lookupFunction(name)?.volatile === true);
}

/** Functions whose natural result is a grid that spills (`UNIQUE`, `SORT`, ...). */
export function arrayFunctionNames(): string[] {
  return allFunctionNames().filter((name) => lookupFunction(name)?.array === true);
}
